/**
 * Projects overview: (1) active target versions, the busiest first; (2) recent activity per day/week/month/year.
 * The numbers come from /api/analytics/*; this page only lays them out.
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../api';
import { useMeta } from '../meta';
import { Avatar, ErrorBox, fmtDate, fmtMinutes, relTime } from '../components';
import { locale, t, tp } from '../i18n';
import { MultiSelect } from './IssueList';

interface Person { id: number; username: string; realname: string }
interface VersionSummary {
  id: number; project_id: number; project_name: string; version: string; date_order: number | null;
  total: number; resolved: number; statuses: Record<string, number>; users: number[];
  last_activity: number | null; recent: number; points_done: number; points_open: number; unestimated: number;
}
interface ActivityUnit {
  start: number; added: number; resolved: number; closed: number; users: number; projects: number;
  story_points: number; needed_minutes: number | null;
}
type Unit = 'day' | 'week' | 'month' | 'year';

const DAY = 86_400_000;
const isoDay = (time: number) => {
  const d = new Date(time);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const personName = (p?: Person) => (p ? p.realname || p.username : '?');

export function AnalyticsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'activity' ? 'activity' : 'versions';
  const go = (next: string) => setParams(next === 'versions' ? {} : { tab: next }, { replace: true });
  return (
    <div>
      <div className="page-head">
        <h1>{t('Projects overview')}</h1>
        <nav className="tabs">
          <a href="?" className={tab === 'versions' ? 'active' : ''} onClick={(e) => { e.preventDefault(); go('versions'); }}>
            {t('Project activities')}
          </a>
          <a href="?tab=activity" className={tab === 'activity' ? 'active' : ''} onClick={(e) => { e.preventDefault(); go('activity'); }}>
            {t('Recent activities')}
          </a>
        </nav>
      </div>
      {tab === 'versions' ? <VersionsOverview /> : <RecentActivity />}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
function VersionsOverview() {
  const { projectId, project, label, statusColor } = useMeta();
  const [weeks, setWeeks] = useState(12);
  const { data, error, isFetching } = useQuery({
    queryKey: ['analytics-versions', projectId, weeks],
    queryFn: () => api.get<{ versions: VersionSummary[]; users: Person[] }>('/api/analytics/versions' + qs({ project: projectId || undefined, weeks })),
  });
  const people = useMemo(() => new Map((data?.users ?? []).map((u) => [u.id, u])), [data]);
  return (
    <section className="card">
      <div className="card-head">
        <h2>
          {t('Active target versions')} <span className="muted">— {projectId ? project(projectId)?.name : t('All Projects')}</span>
          {isFetching && <span className="subtle small"> {t('updating…')}</span>}
        </h2>
        <label className="inline-label small">
          {t('Activity in the last')}
          <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}>
            {[4, 12, 26, 52].map((n) => <option key={n} value={n}>{tp(n, '{n} week', '{n} weeks')}</option>)}
          </select>
        </label>
      </div>
      <ErrorBox error={error} />
      {data && data.versions.length === 0 && <p className="subtle card-body">{t('There are no active target versions.')}</p>}
      {data && data.versions.length > 0 && (
        <div className="table-scroll">
          <table className="grid analytics-versions">
            <thead>
              <tr>
                <th>{t('Project')} / {t('Version')}</th>
                <th>{t('Progress')}</th>
                <th>{t('Status')}</th>
                <th>{t('Involved users')}</th>
                <th>{t('Last activity')}</th>
                <th className="num" title={t('Story points of resolved issues / of open issues')}>{t('Story points (done / open)')}</th>
                <th className="num" title={t('Changes in the last {n} weeks', { n: weeks })}>{t('Changes')}</th>
              </tr>
            </thead>
            <tbody>
              {data.versions.map((v) => {
                const issues = (extra: Record<string, string | number>) =>
                  '/issues' + qs({ project: v.project_id, target_version: v.version, hide_status: 0, ...extra });
                const pct = v.total ? Math.round((100 * v.resolved) / v.total) : 0;
                const statuses = Object.entries(v.statuses).map(([s, n]) => [Number(s), n] as const).sort((a, b) => a[0] - b[0]);
                return (
                  <tr key={v.id}>
                    <td>
                      <div className="subtle small">{v.project_name}</div>
                      <Link to={issues({})}><strong>{v.version}</strong></Link>
                      {v.date_order && <div className="subtle small">{fmtDate(new Date(v.date_order).toISOString(), false)}</div>}
                    </td>
                    <td className="analytics-progress">
                      <div className="status-bar" role="img" aria-label={statuses.map(([s, n]) => `${label('status', s)}: ${n}`).join(', ')}>
                        {statuses.map(([s, n]) => (
                          <Link key={s} to={issues({ status: s })} style={{ flexGrow: n, background: statusColor(s) }} title={`${label('status', s)}: ${n}`} />
                        ))}
                      </div>
                      <div className="small subtle">{t('{done} of {total} resolved ({pct}%)', { done: v.resolved, total: v.total, pct })}</div>
                    </td>
                    <td>
                      <div className="analytics-statuses">
                        {statuses.map(([s, n]) => (
                          <Link key={s} to={issues({ status: s })} className="analytics-status" style={{ borderColor: statusColor(s) }}>
                            {label('status', s)} <strong>{n}</strong>
                          </Link>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="analytics-users">
                        {v.users.map((id) => <Avatar key={id} name={personName(people.get(id))} title={personName(people.get(id))} size={24} />)}
                      </div>
                    </td>
                    <td title={v.last_activity ? fmtDate(new Date(v.last_activity).toISOString()) : undefined}>
                      {v.last_activity ? relTime(new Date(v.last_activity).toISOString()) : '—'}
                    </td>
                    <td className="num">
                      {v.points_done} / {v.points_open}
                      {v.unestimated > 0 && <div className="subtle small">{t('{n} not estimated', { n: v.unestimated })}</div>}
                    </td>
                    <td className="num">{v.recent}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------------------
function RecentActivity() {
  const { meta } = useMeta();
  const [projects, setProjects] = useState<string[]>([]);
  const [users, setUsers] = useState<string[]>([]);
  const [unit, setUnit] = useState<Unit>('week');
  const [from, setFrom] = useState(() => isoDay(Date.now() - 12 * 7 * DAY));
  const [to, setTo] = useState(() => isoDay(Date.now()));
  const range = useMemo(() => {
    const start = new Date(`${from}T00:00:00`), end = new Date(`${to}T00:00:00`);
    end.setDate(end.getDate() + 1); // the end day is included
    return { start, end, valid: !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start < end };
  }, [from, to]);
  const { data, error, isFetching } = useQuery({
    queryKey: ['analytics-activity', projects, users, unit, from, to],
    enabled: range.valid,
    queryFn: () => api.get<{ units: ActivityUnit[]; total: ActivityUnit; people: Person[] }>('/api/analytics/activity' + qs({
      projects: projects.join(',') || undefined, users: users.join(',') || undefined, unit,
      from: range.start.toISOString(), to: range.end.toISOString(), tz: -new Date().getTimezoneOffset(),
    })),
    placeholderData: (previous) => previous,
  });
  const canTime = data?.total.needed_minutes != null;
  const period = (start: number) => {
    const d = new Date(start);
    if (unit === 'year') return String(d.getFullYear());
    if (unit === 'month') return d.toLocaleDateString(locale(), { year: 'numeric', month: 'long' });
    if (unit === 'week') return t('Week of {date}', { date: d.toLocaleDateString(locale(), { day: 'numeric', month: 'short', year: 'numeric' }) });
    return d.toLocaleDateString(locale(), { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };
  // the people to choose from: everyone active in the range, plus those already chosen
  const people = data?.people ?? [];
  const max = Math.max(1, ...(data?.units ?? []).map((u) => Math.max(u.added, u.resolved, u.closed)));
  // small bars behind the counts, relative to the busiest unit (not in the total row)
  const count = (n: number, kind: string, bars: boolean) => (
    <td className="num"><span className="analytics-cell">{n}{bars && <i className={`bar ${kind}`} style={{ width: `${(100 * n) / max}%` }} />}</span></td>
  );
  const row = (u: ActivityUnit, bars = true) => <>
    {count(u.added, 'added', bars)}
    {count(u.resolved, 'resolved', bars)}
    {count(u.closed, 'closed', bars)}
    <td className="num">{u.users}</td>
    <td className="num">{u.projects}</td>
    <td className="num">{u.story_points}</td>
    {canTime && <td className="num">{u.needed_minutes ? fmtMinutes(u.needed_minutes) : '0'}</td>}
  </>;
  return (
    <>
      <section className="card filters analytics-filters">
        <div className="analytics-filter-row">
          <MultiSelect label={t('Projects')} options={meta.projects.map((p) => ({ value: String(p.id), label: p.name }))} value={projects} onChange={setProjects} />
          <MultiSelect
            label={t('Users')}
            options={[...people.map((p) => ({ value: String(p.id), label: personName(p) })),
              ...users.filter((id) => !people.some((p) => String(p.id) === id)).map((id) => ({ value: id, label: `#${id}` }))]}
            value={users}
            onChange={setUsers}
          />
          <label className="inline-label small">
            {t('Per')}
            <select value={unit} onChange={(e) => setUnit(e.target.value as Unit)}>
              <option value="day">{t('Day')}</option>
              <option value="week">{t('Week')}</option>
              <option value="month">{t('Month')}</option>
              <option value="year">{t('Year')}</option>
            </select>
          </label>
          <label className="inline-label small">
            {t('From')}
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="inline-label small">
            {t('To')}
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </label>
          {isFetching && <span className="subtle small">{t('updating…')}</span>}
        </div>
      </section>
      <ErrorBox error={error} />
      {data && (
        <section className="card">
          <div className="table-scroll">
            <table className="grid analytics-activity">
              <thead>
                <tr>
                  <th>{t('Period')}</th>
                  <th className="num">{t('Added issues')}</th>
                  <th className="num" title={t('Issues that became resolved')}>{t('Finished (resolved)')}</th>
                  <th className="num" title={t('Issues that became closed')}>{t('Tested (closed)')}</th>
                  <th className="num">{t('Involved users')}</th>
                  <th className="num">{t('Involved projects')}</th>
                  <th className="num" title={t('Story points of the issues resolved in the period')}>{t('Story points')}</th>
                  {canTime && <th className="num" title={t('Needed time of the issues resolved in the period')}>{t('Needed time')}</th>}
                </tr>
              </thead>
              <tbody>
                {[...data.units].reverse().map((u) => (
                  <tr key={u.start}>
                    <td>{period(u.start)}</td>
                    {row(u)}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>{t('Total')}</td>
                  {row(data.total, false)}
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="subtle small card-body">
            {t('An issue resolved or closed more than once counts once, at the last time. Story points and needed time belong to the period in which the issue was resolved.')}
          </p>
        </section>
      )}
    </>
  );
}
